import React, { useEffect } from 'react';
import { Card, Toggle, TextField, Tooltip } from '@nike/eds';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import NikeStoreTealIcon from 'assets/img/Nike-Store-Icon-Teal.png';
import SwooshTealIcon from 'assets/img/Swoosh-Icon-Teal.png';
import ConverseTealIcon from 'assets/img/Converse-Star-Teal.png';
import NikeStoreGreyIcon from 'assets/img/Nike-Store-Icon-Grey.png';
import SwooshGreyIcon from 'assets/img/Swoosh-Icon-Grey.png';
import ConverseGreyIcon from 'assets/img/Converse-Star-Grey.png';
import SwooshYellowIcon from 'assets/img/Swoosh-Icon-Yellow.svg';
import { useI18nContext } from 'context/language/use-i18n-context';
import ts from './i18n';
import styles from './manage-access.module.css';
import { AppConfig } from 'utils/constants';
import { ManageAccessModal } from 'models/IManageAccess';

interface ManageAccessProps {
  manageAccess: ManageAccessModal;
  manageAccessStateChange: (state: ManageAccessModal) => void;
}

export type { ManageAccessProps };

export const ManageAccess: React.FC<ManageAccessProps> = ({
  manageAccess,
  manageAccessStateChange,
}) => {
  const { i18nString } = useI18nContext();

  const [manageAccessState, setManageAccessState] = React.useState<ManageAccessModal>(manageAccess);
  const getValidationSchema = (manageAccessModel: ManageAccessModal) => {
    return Yup.object({
      storeEmail: Yup.string().email('Invalid email address'),
      swooshEmail: manageAccessModel.passes.swoosh.isActive
        ? manageAccessModel.showSwoosh
          ? manageAccessModel.isChina
            ? Yup.string()
                .required('Required')
                .matches(RegExp(AppConfig.regexPatterns.regexMobile), 'Invalid mobile number')
            : Yup.string().email('Invalid email address').required('Required')
          : Yup.string()
        : Yup.string().notRequired(), // Skip validation if showSwoosh is false
      converseEmail: manageAccessModel.passes.converse.isActive
        ? manageAccessModel.showConverse
          ? Yup.string().email('Invalid email address').required('Required')
          : Yup.string()
        : Yup.string().notRequired(), // Skip validation if showConverse is false
    });
  };
  const [validation, setValidationSchema] = React.useState(() =>
    getValidationSchema(manageAccessState),
  );
  const formik = useFormik({
    initialValues: {
      storeEmail: manageAccess.passes.store.email,
      swooshEmail: manageAccess.passes.swoosh.email,
      converseEmail: manageAccess.passes.converse.email,
    },
    validationSchema: validation,
    onSubmit: () => {
      return;
    },
  });

  // Reset the Formik form state when manageAccess changes
  // This ensures that the form reflects the latest state of manageAccess
  useEffect(() => {
    setManageAccessState(manageAccess);
    // Reset the Formik form state
    formik.resetForm({
      values: {
        storeEmail: manageAccess.passes.store.email,
        swooshEmail: manageAccess.passes.swoosh.email,
        converseEmail: manageAccess.passes.converse.email,
      },
      errors: {}, // Clear all errors
      touched: {}, // Clear all touched fields
    });
  }, [manageAccess]);

  const getTextClass = (isActive: boolean, isPending: boolean) => {
    if (isPending && isActive) return styles.text_pending;
    return isActive ? styles.text_active : styles.text_inactive;
  };

  const getToggleClass = (isActive: boolean, isPending: boolean) => {
    if (isActive && isPending) return styles.toggle_pending;
    return isActive ? styles.toggle_active : styles.toggle_inactive;
  };

  const handleToggleChange = async (field: keyof ManageAccessModal['passes'], value: boolean) => {
    const prevState = manageAccessState;
    const accessState = {
      ...prevState,
      passes: {
        ...prevState.passes,
        [field]: {
          ...prevState.passes[field],
          isActive: value,
        },
      },
    };
    const updatedValidationSchema = getValidationSchema(accessState);
    await setValidationSchema(updatedValidationSchema);
    const errors = await formik.validateForm();
    const isFormValid = Object.keys(errors).length === 0;
    setManageAccessState(prevState => ({
      ...prevState,
      isValid: isFormValid,
      passes: {
        ...prevState.passes,
        [field]: {
          ...prevState.passes[field],
          isActive: value,
        },
      },
    }));
  };

  const handleInputChange = async (field: keyof ManageAccessModal['passes'], value: string) => {
    // Update Formik value
    await formik.setFieldValue(field + 'Email', value);

    // Validate the entire form to get the latest validity
    const errors = await formik.validateForm();

    // Determine if the form is valid
    const isFormValid = Object.keys(errors).length === 0;

    // Update parent state with the latest form validity and values
    setManageAccessState(prevState => ({
      ...prevState,
      isValid: isFormValid, //This flag will help to understand if the form is valid or not and enable/disable the submit button
      passes: {
        ...prevState.passes,
        [field]: {
          ...prevState.passes[field],
          email: value,
        },
      },
    }));
  };

  useEffect(() => {
    manageAccessStateChange(manageAccessState);
  }, [manageAccessState, manageAccessStateChange]);

  const getSwooshToolTipMessage = (isActive: boolean) => {
    if (manageAccessState.isChina && isActive) {
      if (manageAccessState?.passes?.swoosh?.isDisabled) {
        return 'Need to update your registered swoosh account mobile number? Visit account settings on your swoosh account to update';
      }
      return 'Your mobile number';
    }

    if (manageAccessState.isChina && !isActive) {
      return 'Your mobile number';
    }

    if (isActive) {
      if (manageAccessState?.passes?.swoosh?.isDisabled) {
        return 'Need to update your registered swoosh account email? Visit account settings on your swoosh account to update';
      }
      return 'Your email';
    }

    return 'Your email';
  };

  const getSwooshToogleToolTip = () => {
    if (manageAccessState.passes.swoosh.isPendingApproval) return 'Pending your Manager Approval';
    if (manageAccessState.passes.swoosh.isActive) {
      return 'ON';
    }
    if (!manageAccessState.passes.swoosh.isActive) {
      return 'OFF';
    }

    return '';
  };

  return (
    <FormikProvider value={formik}>
      <Card>
        <div className={styles.access_row}>
          {manageAccessState.passes.store.isDisabled ? (
            <Tooltip
              isDark={true}
              placement={'top'}
              bodySlot={'Store pass inactivation not allowed'}
            >
              <div className={getToggleClass(manageAccessState.passes.store.isActive, false)}>
                <Toggle
                  id="storeToggle"
                  data-testid="storeToggle"
                  label=""
                  className={styles.access_toggle}
                  checked={manageAccessState.passes.store.isActive}
                  onChange={event => handleToggleChange('store', event.target.checked)}
                  disabled={manageAccess.passes.store.isDisabled}
                />
              </div>
            </Tooltip>
          ) : (
            <div className={getToggleClass(manageAccessState.passes.store.isActive, false)}>
              <Toggle
                id="storeToggle"
                label=""
                data-testid="storeToggleActive"
                className={styles.access_toggle}
                checked={manageAccessState.passes.store.isActive}
                onChange={event => handleToggleChange('store', event.target.checked)}
                disabled={manageAccess.passes.store.isDisabled}
              />
            </div>
          )}
          <Tooltip
            isDark={true}
            placement={'top'}
            enableFocus
            bodySlot={
              (manageAccessState.passes.store.isActive ? 'Active' : 'Inactive') + ' Store Pass'
            }
          >
            <div className={styles.access_box_container}>
              <img
                className={styles.access_store_img}
                src={
                  manageAccessState.passes.store.isActive ? NikeStoreTealIcon : NikeStoreGreyIcon
                }
                alt="Nike Store Icon"
              />
              <span
                className={`${styles.access_box_text} ${getTextClass(manageAccessState.passes.store.isActive, false)}`}
              >
                STORES
              </span>
            </div>
          </Tooltip>
          <TextField
            id="storeEmail"
            label={i18nString(ts.YOUR_EMAIL_TEXT)}
            aria-label={i18nString(ts.YOUR_EMAIL_TEXT)}
            hideLabel={true}
            placeholder={i18nString(ts.YOUR_EMAIL_TEXT)}
            type="text"
            name="storeEmail"
            data-testid="storeEmail"
            className={styles.access_textField}
            onChange={event => {
              formik.handleChange(event);
              handleInputChange('store', event.target.value);
            }}
            onBlur={formik.handleBlur}
            hasErrors={
              manageAccessState.passes.store.isActive &&
              formik.touched.storeEmail &&
              !!formik.errors.storeEmail
            }
            errorMessage={manageAccessState.passes.store.isActive ? formik.errors.storeEmail : ''}
            value={formik.values.storeEmail}
            disabled={
              !manageAccessState.passes.store.isActive || manageAccess.passes.store.isEmailDisabled
            }
          />
        </div>
        {manageAccessState.showConverse && (
          <div className={styles.access_row}>
            <div className={getToggleClass(manageAccessState.passes.converse.isActive, false)}>
              <Tooltip
                isDark={true}
                placement={'top'}
                enableFocus
                bodySlot={manageAccessState.passes.converse.isActive ? 'ON' : 'OFF'}
              >
                <Toggle
                  id="converseToggle"
                  data-testid="converseToggle"
                  label=""
                  className={styles.access_toggle}
                  checked={manageAccessState.passes.converse.isActive}
                  onChange={event => handleToggleChange('converse', event.target.checked)}
                />
              </Tooltip>
            </div>
            <Tooltip
              isDark={true}
              placement={'top'}
              enableFocus
              bodySlot={
                (manageAccessState.passes.converse.isActive ? 'Active' : 'Inactive') +
                ' Converse Pass'
              }
            >
              <div className={styles.access_box_container}>
                <img
                  className={styles.access_converse_img}
                  src={
                    manageAccessState.passes.converse.isActive ? ConverseTealIcon : ConverseGreyIcon
                  }
                  alt="Converse Icon"
                />
                <span
                  className={`${styles.access_box_text} ${getTextClass(manageAccessState.passes.converse.isActive, false)}`}
                >
                  CONVERSE.COM
                </span>
              </div>
            </Tooltip>
            <TextField
              id="converseEmail"
              label={i18nString(ts.YOUR_EMAIL_TEXT)}
              aria-label={i18nString(ts.YOUR_EMAIL_TEXT)}
              hideLabel={true}
              placeholder={i18nString(ts.YOUR_EMAIL_TEXT)}
              type="text"
              name="converseEmail"
              data-testid="converseEmail"
              className={styles.access_textField}
              onChange={event => {
                formik.handleChange(event);
                handleInputChange('converse', event.target.value);
              }}
              onBlur={formik.handleBlur}
              hasErrors={
                manageAccessState.passes.converse.isActive &&
                formik.touched.converseEmail &&
                !!formik.errors.converseEmail
              }
              errorMessage={
                manageAccessState.passes.converse.isActive ? formik.errors.converseEmail : ''
              }
              value={formik.values.converseEmail}
              disabled={!manageAccessState.passes.converse.isActive}
            />
          </div>
        )}
        {manageAccessState.showSwoosh && (
          <div className={styles.access_row}>
            <Tooltip
              isDark={true}
              placement={'top'}
              //bodySlot={"Swoosh pass inactivation not allowed in pending state"}
              bodySlot={getSwooshToogleToolTip()}
            >
              <div
                className={getToggleClass(
                  manageAccessState.passes.swoosh.isActive,
                  manageAccessState.passes.swoosh.isPendingApproval,
                )}
              >
                <Toggle
                  id="swooshToggle"
                  data-testid="swooshToggle"
                  label=""
                  className={styles.access_toggle}
                  checked={manageAccessState.passes.swoosh.isActive}
                  onChange={event => handleToggleChange('swoosh', event.target.checked)}
                  // disabled={manageAccess.passes.swoosh.isDisabled}
                />
              </div>
            </Tooltip>
            <Tooltip
              isDark={true}
              placement={'top'}
              enableFocus
              bodySlot={
                manageAccessState.passes.swoosh.isPendingApproval
                  ? 'Manager Approval Pending'
                  : (manageAccessState.passes.swoosh.isActive ? 'Active' : 'Inactive') +
                    ' Swoosh Pass'
              }
            >
              <div className={styles.access_box_container}>
                <img
                  className={styles.access_swoosh_img}
                  src={
                    manageAccessState.passes.swoosh.isPendingApproval &&
                    manageAccessState.passes.swoosh.isActive
                      ? SwooshYellowIcon
                      : manageAccessState.passes.swoosh.isActive
                        ? SwooshTealIcon
                        : SwooshGreyIcon
                  }
                  alt="Swoosh Icon"
                />
                <span
                  className={`${styles.access_box_text} ${getTextClass(manageAccessState.passes.swoosh.isActive, manageAccessState.passes.swoosh.isPendingApproval)}`}
                >
                  SWOOSH.COM
                </span>
              </div>
            </Tooltip>
            <Tooltip
              isDark={true}
              placement={'top'}
              enableFocus
              bodySlot={getSwooshToolTipMessage(manageAccessState.passes.swoosh.isActive)}
            >
              <TextField
                id="swooshEmail"
                label={
                  manageAccess.isChina
                    ? i18nString(ts.YOUR_SWOOSH_Mobile_TEXT)
                    : i18nString(ts.YOUR_EMAIL_TEXT)
                }
                aria-label={
                  manageAccess.isChina
                    ? i18nString(ts.YOUR_SWOOSH_Mobile_TEXT)
                    : i18nString(ts.YOUR_EMAIL_TEXT)
                }
                hideLabel={true}
                placeholder={
                  manageAccess.isChina
                    ? i18nString(ts.YOUR_SWOOSH_Mobile_TEXT)
                    : i18nString(ts.YOUR_EMAIL_TEXT)
                }
                type="text"
                name="swooshEmail"
                className={styles.access_textField}
                data-testid="swooshEmail"
                onChange={event => {
                  formik.handleChange(event);
                  handleInputChange('swoosh', event.target.value);
                }}
                onBlur={formik.handleBlur}
                hasErrors={
                  manageAccessState.passes.swoosh.isActive &&
                  formik.touched.swooshEmail &&
                  !!formik.errors.swooshEmail
                }
                errorMessage={
                  manageAccessState.passes.swoosh.isActive ? formik.errors.swooshEmail : ''
                }
                value={formik.values.swooshEmail}
                disabled={
                  manageAccessState.passes.swoosh.isDisabled ||
                  !manageAccessState.passes.swoosh.isActive
                }
              />
            </Tooltip>
          </div>
        )}
      </Card>
    </FormikProvider>
  );
};

export default ManageAccess;
